import { useState, useEffect, useRef, useCallback } from 'react';
import { FeaturePlate } from '../FeaturePlate';
import './RotatingFeaturePlate.css';

const ROTATE_INTERVAL = 7000; // ms between advances
const FADE_MS         = 600;  // opacity transition duration

/**
 * RotatingFeaturePlate
 *
 * Cycles through a pool of featured listings using a smooth opacity fade.
 * Pauses on hover; supports keyboard navigation (←/→) and dot-click.
 *
 * @param {Array}  pool   - Array of listing objects to rotate through
 * @param {string} badge  - Badge label shown on each card (default "Tonight's Featured")
 */
export function RotatingFeaturePlate({ pool = [], badge = "Tonight's Featured" }) {
  const [index, setIndex]   = useState(0);
  const [visible, setVisible] = useState(true);
  const timerRef            = useRef(null);
  const pausedRef           = useRef(false);

  const advance = useCallback((dir = 1) => {
    setVisible(false);
    setTimeout(() => {
      setIndex((i) => (i + dir + pool.length) % pool.length);
      setVisible(true);
    }, FADE_MS);
  }, [pool.length]);

  const startTimer = useCallback(() => {
    if (pool.length <= 1) return;
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      if (!pausedRef.current) advance(1);
    }, ROTATE_INTERVAL);
  }, [advance, pool.length]);

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
  }, [startTimer]);

  // Reset to first card whenever the pool changes (new data loaded)
  useEffect(() => {
    setIndex(0);
    setVisible(true);
  }, [pool]);

  if (!pool.length) return null;

  const listing = pool[index];
  const isPromoted = listing.promotion?.isActive;
  const displayBadge = isPromoted ? badge : 'Featured';

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft')  advance(-1);
    if (e.key === 'ArrowRight') advance(1);
  };

  return (
    <div
      className="rotating-feature-plate"
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { pausedRef.current = false; }}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      aria-label="Featured business carousel"
      role="region"
    >
      <div
        className="rotating-feature-plate__card"
        style={{
          opacity: visible ? 1 : 0,
          transition: `opacity ${FADE_MS}ms ease-in-out`,
        }}
      >
        <FeaturePlate
          badge={displayBadge}
          title={listing.name}
          meta={[listing.category, listing.location].filter(Boolean).join(' · ')}
          blurb={listing.description}
          ctaLabel="Visit Listing →"
          ctaHref={`/business-listings/${listing.id}`}
        />
      </div>

      {pool.length > 1 && (
        <div className="rotating-feature-plate__dots" aria-hidden="true">
          {pool.map((_, i) => (
            <button
              key={i}
              className={`rotating-feature-plate__dot${i === index ? ' active' : ''}`}
              onClick={() => {
                const dir = i - index;
                if (dir !== 0) advance(dir);
                startTimer(); // restart timer on manual nav
              }}
              aria-label={`Go to listing ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default RotatingFeaturePlate;
